import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useCreateUser, useUpdateUser, useFonti } from '@/lib/queries';
import { usernameSchema, passwordSchema } from '@shared/schemas';
import type { CreateUserInput, UpdateUserInput } from '@shared/schemas';

type Initial = {
  username?: string | null;
  role?: 'admin' | 'user';
  banned?: boolean;
  allowedFonti?: string[] | null;
  dataFrom?: string | null;
  dataTo?: string | null;
};

type Props = {
  /** Presente in modifica; assente in creazione. */
  userId?: string;
  initial?: Initial;
};

function generatePassword(): string {
  const charset = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const arr = new Uint32Array(20);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => charset[n % charset.length]).join('');
}

export function UserForm({ userId, initial }: Props) {
  const navigate = useNavigate();
  const create = useCreateUser();
  const update = useUpdateUser();
  const fonti = useFonti();
  const isEdit = !!userId;

  const [username, setUsername] = useState(initial?.username ?? '');
  const [password, setPassword] = useState('');
  const [resetPassword, setResetPassword] = useState(false);
  const [role, setRole] = useState<'admin' | 'user'>(initial?.role ?? 'user');
  const [banned, setBanned] = useState(initial?.banned ?? false);
  const [selectedFonti, setSelectedFonti] = useState<string[]>(initial?.allowedFonti ?? []);
  const [dataFrom, setDataFrom] = useState(initial?.dataFrom ?? '');
  const [dataTo, setDataTo] = useState(initial?.dataTo ?? '');
  const [error, setError] = useState<string | null>(null);
  const [justSaved, setJustSaved] = useState(false);

  const availableFonti = fonti.data?.fonti ?? [];

  function toggleFonte(f: string) {
    setSelectedFonti((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Validazioni client
    if (!isEdit) {
      if (!usernameSchema.safeParse(username).success) {
        setError('Username: 3–30 caratteri, solo lettere, numeri e underscore');
        return;
      }
      if (!passwordSchema.safeParse(password).success) {
        setError('Inserisci una password');
        return;
      }
    } else if (resetPassword && !passwordSchema.safeParse(password).success) {
      setError('Inserisci la nuova password');
      return;
    }
    if (role === 'user' && selectedFonti.length === 0) {
      setError('Seleziona almeno una fonte per un lettore');
      return;
    }

    // I permessi fonte/periodo valgono solo per i lettori (gli admin vedono tutto).
    const allowedFonti = role === 'user' ? selectedFonti : [];
    const from = role === 'user' ? dataFrom || null : null;
    const to = role === 'user' ? dataTo || null : null;

    try {
      if (isEdit) {
        const input: UpdateUserInput = {
          role,
          banned,
          banReason: banned ? 'Sospeso da admin' : null,
          allowedFonti,
          dataFrom: from,
          dataTo: to,
          ...(resetPassword && password ? { newPassword: password } : {}),
        };
        await update.mutateAsync({ id: userId, input });
        setJustSaved(true);
        setResetPassword(false);
        setPassword('');
        setTimeout(() => setJustSaved(false), 2500);
      } else {
        const input: CreateUserInput = {
          username,
          password,
          role,
          allowedFonti,
          dataFrom: from,
          dataTo: to,
        };
        await create.mutateAsync(input);
        navigate('/admin/utenti');
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Errore salvataggio utente');
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Username */}
      <div className="space-y-1.5">
        <Label htmlFor="username">Username</Label>
        <Input
          id="username"
          type="text"
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          required={!isEdit}
          disabled={isEdit}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          placeholder="es. mrossi"
        />
        <p className="text-xs text-(--color-muted-foreground)">
          {isEdit
            ? "Lo username non è modificabile dopo la creazione."
            : '3–30 caratteri: lettere, numeri e underscore. Verrà reso minuscolo.'}
        </p>
      </div>

      {/* Password */}
      {!isEdit ? (
        <div className="space-y-1.5">
          <Label htmlFor="password">Password</Label>
          <div className="flex gap-2">
            <Input
              id="password"
              type="text"
              required
              minLength={1}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="font-mono"
            />
            <Button type="button" variant="outline" onClick={() => setPassword(generatePassword())}>
              Genera
            </Button>
          </div>
          <p className="text-xs text-(--color-muted-foreground)">
            La password viene mostrata in chiaro: copiala e condividila con l'utente in modo sicuro.
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={resetPassword}
              onChange={(e) => {
                setResetPassword(e.target.checked);
                if (!e.target.checked) setPassword('');
              }}
            />
            Reimposta la password
          </label>
          {resetPassword && (
            <>
              <div className="flex gap-2">
                <Input
                  id="newPassword"
                  type="text"
                  minLength={1}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="font-mono"
                  placeholder="Nuova password"
                />
                <Button type="button" variant="outline" onClick={() => setPassword(generatePassword())}>
                  Genera
                </Button>
              </div>
              <p className="text-xs text-(--color-muted-foreground)">
                Le sessioni attive dell'utente verranno invalidate.
              </p>
            </>
          )}
        </div>
      )}

      {/* Ruolo */}
      <div className="space-y-1.5">
        <Label htmlFor="role">Ruolo</Label>
        <Select value={role} onValueChange={(v) => setRole(v as 'admin' | 'user')}>
          <SelectTrigger id="role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="user">Lettore — accesso limitato in sola lettura</SelectItem>
            <SelectItem value="admin">Amministratore — gestisce articoli e utenti, vede tutto</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Permessi (solo per i lettori) */}
      {role === 'user' ? (
        <div className="space-y-5 rounded-md border border-(--color-border) p-4">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Fonti consentite</Label>
              <div className="flex gap-3 text-xs">
                <button
                  type="button"
                  className="text-(--color-muted-foreground) underline-offset-2 hover:underline"
                  onClick={() => setSelectedFonti(availableFonti)}
                >
                  Tutte
                </button>
                <button
                  type="button"
                  className="text-(--color-muted-foreground) underline-offset-2 hover:underline"
                  onClick={() => setSelectedFonti([])}
                >
                  Nessuna
                </button>
              </div>
            </div>
            {fonti.isLoading ? (
              <p className="text-sm text-(--color-muted-foreground)">Caricamento fonti…</p>
            ) : availableFonti.length === 0 ? (
              <p className="text-sm text-(--color-muted-foreground)">Nessuna fonte disponibile.</p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {availableFonti.map((f) => {
                  const active = selectedFonti.includes(f);
                  return (
                    <button
                      type="button"
                      key={f}
                      aria-pressed={active}
                      onClick={() => toggleFonte(f)}
                      className={cn(
                        'inline-flex items-center gap-1 rounded-full border px-3 py-1 text-sm transition-colors',
                        active
                          ? 'border-(--color-brand-terracotta) bg-(--color-brand-gold) text-(--color-foreground)'
                          : 'border-(--color-border) bg-(--color-background) text-(--color-muted-foreground) hover:bg-(--color-muted)',
                      )}
                    >
                      {active && <Check className="h-3.5 w-3.5" />}
                      {f}
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-(--color-muted-foreground)">
              Il lettore vedrà solo gli articoli delle fonti selezionate. Almeno una è obbligatoria.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="dataFrom">Dal (opzionale)</Label>
              <DatePicker
                id="dataFrom"
                value={dataFrom}
                onChange={setDataFrom}
                placeholder="Nessun limite"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="dataTo">Al (opzionale)</Label>
              <DatePicker
                id="dataTo"
                value={dataTo}
                onChange={setDataTo}
                placeholder="Nessun limite"
              />
            </div>
          </div>
          {(dataFrom || dataTo) && (
            <button
              type="button"
              className="text-xs text-(--color-muted-foreground) underline-offset-2 hover:underline"
              onClick={() => {
                setDataFrom('');
                setDataTo('');
              }}
            >
              Azzera periodo
            </button>
          )}
        </div>
      ) : (
        <p className="rounded-md border border-(--color-border) bg-(--color-muted)/40 p-3 text-sm text-(--color-muted-foreground)">
          Gli amministratori vedono tutte le fonti e tutti i periodi.
        </p>
      )}

      {/* Sospeso */}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={banned} onChange={(e) => setBanned(e.target.checked)} />
        Sospeso (non può accedere)
      </label>

      {error && (
        <p className="text-sm text-(--color-destructive)" role="alert">
          {error}
        </p>
      )}

      <div className="flex items-center justify-end gap-2 border-t border-(--color-border) pt-5">
        <Button type="button" variant="outline" onClick={() => navigate('/admin/utenti')}>
          Annulla
        </Button>
        <Button type="submit" disabled={pending}>
          {pending ? (
            'Salvataggio…'
          ) : justSaved && isEdit ? (
            <>
              <Check className="h-4 w-4" /> Salvato
            </>
          ) : isEdit ? (
            'Salva modifiche'
          ) : (
            'Crea utente'
          )}
        </Button>
      </div>
    </form>
  );
}
